import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, AlertTriangle } from 'lucide-react'
import {
  useStudentTextbookStatus,
  useTextbookCatalog,
  useCreateTextbookRequest,
  useUpdateTextbookRequestStatus,
} from '@/hooks/admin/useTextbookRequests'
import type { LcTextbookCatalogView, TextbookAlertView, TextbookRequestView } from '@/api/textbookRequests'
import { LcTextbookStatusActionDialog } from './LcTextbookStatusActionDialog'

/**
 * 교재 신청 관리 패널 (Phase 1: 학생별 단건 요청, 발송 결과 수동 반영).
 * spec: docs/features/lc-admin/student-textbook-order-management-spec.md §6-1
 */
export function TextbookRequestPanel({ userId }: { userId: number }) {
  const { t } = useTranslation(['admin'])
  const { data, isLoading } = useStudentTextbookStatus(userId)
  const { data: catalog = [], isLoading: isCatalogLoading } = useTextbookCatalog(data?.curriculumId ?? null)
  const createMutation = useCreateTextbookRequest(userId)
  const updateMutation = useUpdateTextbookRequestStatus(userId)
  const [statusAction, setStatusAction] = useState<{
    request: TextbookRequestView
    nextStatus: 'RECEIVED' | 'CANCELLED'
  } | null>(null)

  if (isLoading || !data) return null
  const { currentBooks, alerts, requests, contentFeeBlockedReason } = data
  if (currentBooks.length === 0 && requests.length === 0 && catalog.length === 0 && !isCatalogLoading) return null

  const activeRequestsByBookId = new Map(
    requests
      .filter((request) =>
        ['REQUESTED_TO_HQ', 'OUTBOUND_REQUESTING', 'OUTBOUND_REQUESTED', 'OUTBOUND_UNCERTAIN', 'SHIPPED']
          .includes(request.status),
      )
      .map((request) => [request.printBookId, request]),
  )
  const alertsByBookId = new Map(alerts.map((alert) => [alert.printBookId, alert]))

  const handleRequest = (alert: TextbookAlertView) => {
    if (alert.onLeave && !window.confirm(t('admin:students.detail.textbookRequest.onLeaveConfirm'))) {
      return
    }
    createMutation.mutate({
      printBookId: alert.printBookId,
      requestAttemptId: crypto.randomUUID(),
      memo: null,
    })
  }

  const handleCatalogRequest = (book: LcTextbookCatalogView) => {
    const alert = alertsByBookId.get(book.bookId)
    if (alert) {
      handleRequest(alert)
      return
    }
    createMutation.mutate({
      printBookId: book.bookId,
      requestAttemptId: crypto.randomUUID(),
      memo: null,
    })
  }

  const handleStatus = (request: TextbookRequestView, status: 'RECEIVED' | 'CANCELLED') => {
    setStatusAction({ request, nextStatus: status })
  }

  return (
    <section className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <BookOpen className="h-4 w-4 text-primary-600" />
        {t('admin:students.detail.textbookRequest.title')}
      </h3>

      {/* 현재 교재 + 진도 */}
      {currentBooks.map((book) => (
        <div key={book.bookId} className="text-sm text-gray-700">
          <span className="font-medium">{t('admin:students.detail.textbookRequest.currentBook')}: </span>
          {book.title}
          {book.volumeLabel ? ` (${book.volumeLabel})` : ''} ·{' '}
          {t('admin:students.detail.textbookRequest.progress', {
            total: book.totalLessons,
            rank: book.currentLessonRank,
          })}
          {book.lastVolume ? (
            <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
              {t('admin:students.detail.textbookRequest.lastVolume')}
            </span>
          ) : null}
        </div>
      ))}

      {/* 알람 + 요청 버튼 */}
      {alerts.map((alert) => (
        <div
          key={`${alert.reason}-${alert.printBookId}`}
          className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="text-sm text-amber-800">
            {t(`admin:students.detail.textbookRequest.alertTitle.${alert.reason}`)}:{' '}
            <strong>
              {alert.bookTitle}
              {alert.volumeLabel ? ` (${alert.volumeLabel})` : ''}
            </strong>
          </span>
          {alert.gracePeriod ? (
            <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs text-orange-700">
              {t('admin:students.detail.textbookRequest.badges.grace')}
            </span>
          ) : null}
          {alert.onLeave ? (
            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs text-blue-700">
              {t('admin:students.detail.textbookRequest.badges.onLeave')}
            </span>
          ) : null}
          {alert.existingRequestStatus ? (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
              {t(`admin:students.detail.textbookRequest.status.${alert.existingRequestStatus}`)}
            </span>
          ) : (
            <button
              type="button"
              disabled={!alert.requestable || createMutation.isPending}
              title={
                alert.blockedReason
                  ? t(`admin:students.detail.textbookRequest.blocked.${alert.blockedReason}`)
                  : undefined
              }
              onClick={() => handleRequest(alert)}
              className="ml-auto rounded-lg bg-primary-600 px-3 py-1.5 text-xs text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {t('admin:students.detail.textbookRequest.requestButton')}
            </button>
          )}
        </div>
      ))}

      {/* 요청 이력 */}
      <div>
        <p className="mb-1 text-xs font-medium text-gray-500">
          {t('admin:students.detail.textbookRequest.history')}
        </p>
        {requests.length === 0 ? (
          <p className="text-xs text-gray-400">
            {t('admin:students.detail.textbookRequest.historyEmpty')}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {requests.map((req) => (
              <li key={req.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
                <span>
                  {req.bookTitle ?? `#${req.printBookId}`}
                  {req.volumeLabel ? ` (${req.volumeLabel})` : ''}
                </span>
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                  {t(`admin:students.detail.textbookRequest.status.${req.status}`)}
                </span>
                {req.inGracePeriod ? (
                  <span className="rounded bg-orange-100 px-1.5 py-0.5 text-xs text-orange-700">
                    {t('admin:students.detail.textbookRequest.badges.grace')}
                  </span>
                ) : null}
                <span className="text-xs text-gray-400">
                  {new Date(req.requestedAt).toLocaleDateString()}
                </span>
                {req.memo ? <span className="text-xs text-gray-500">{req.memo}</span> : null}
                {req.statusMessage ? <span className="text-xs text-gray-500">{req.statusMessage}</span> : null}
                <span className="ml-auto flex gap-1">
                  {req.status === 'REQUESTED_TO_HQ' ? (
                    <button
                      type="button"
                      disabled={updateMutation.isPending}
                      onClick={() => handleStatus(req, 'CANCELLED')}
                      className="rounded border border-gray-200 px-2 py-0.5 text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('admin:students.detail.textbookRequest.actions.cancel')}
                    </button>
                  ) : null}
                  {req.status === 'SHIPPED' ? (
                    <button
                      type="button"
                      disabled={updateMutation.isPending}
                      onClick={() => handleStatus(req, 'RECEIVED')}
                      className="rounded border border-gray-200 px-2 py-0.5 text-xs hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('admin:students.detail.textbookRequest.actions.receive')}
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 수강 교재 목록 */}
      <div className="border-t border-gray-100 pt-3">
        <p className="mb-2 text-xs font-medium text-gray-500">
          {t('admin:students.detail.textbookRequest.catalogTitle')}
        </p>
        {isCatalogLoading ? (
          <p className="text-xs text-gray-400">
            {t('admin:students.detail.textbookRequest.catalogLoading')}
          </p>
        ) : catalog.length === 0 ? (
          <p className="text-xs text-gray-400">
            {t('admin:students.detail.textbookRequest.catalogEmpty')}
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {catalog.map((book) => {
              const activeRequest = activeRequestsByBookId.get(book.bookId)
              const alert = alertsByBookId.get(book.bookId)
              const blockedReason = alert?.blockedReason ?? contentFeeBlockedReason
              const requestDisabled =
                createMutation.isPending || !!activeRequest || alert?.requestable === false || !!contentFeeBlockedReason
              return (
                <li
                  key={book.bookId}
                  data-testid={`textbook-catalog-row-${book.bookId}`}
                  className="flex flex-wrap items-center gap-2 py-2 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-800">
                      {book.title}
                      {book.volumeLabel ? ` (${book.volumeLabel})` : ''}
                    </div>
                    <div className="mt-0.5 flex flex-wrap gap-2 text-xs text-gray-500">
                      <span>
                        {t(`admin:students.detail.textbookRequest.materialType.${book.materialType}`, {
                          defaultValue: book.materialType,
                        })}
                      </span>
                      <span>{t('admin:students.detail.textbookRequest.lessonCount', { count: book.totalLessons })}</span>
                      {book.nextBookId == null ? (
                        <span>{t('admin:students.detail.textbookRequest.lastVolume')}</span>
                      ) : null}
                    </div>
                  </div>
                  {activeRequest ? (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                      {t(`admin:students.detail.textbookRequest.status.${activeRequest.status}`)}
                    </span>
                  ) : null}
                  {!activeRequest ? (
                    <button
                      type="button"
                      disabled={requestDisabled}
                      title={
                        blockedReason
                          ? t(`admin:students.detail.textbookRequest.blocked.${blockedReason}`)
                          : undefined
                      }
                      onClick={() => handleCatalogRequest(book)}
                      className="rounded-lg border border-primary-200 px-3 py-1.5 text-xs text-primary-700 hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                    >
                      {t('admin:students.detail.textbookRequest.requestButton')}
                    </button>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {statusAction ? (
        <LcTextbookStatusActionDialog
          key={`${statusAction.request.id}:${statusAction.nextStatus}`}
          request={statusAction.request}
          nextStatus={statusAction.nextStatus}
          pending={updateMutation.isPending}
          onClose={() => setStatusAction(null)}
          onConfirm={(reason) => updateMutation.mutate(
            {
              requestId: statusAction.request.id,
              body: {
                status: statusAction.nextStatus,
                expectedStatus: statusAction.request.status,
                attemptId: crypto.randomUUID(),
                reason,
              },
            },
            { onSuccess: () => setStatusAction(null) },
          )}
        />
      ) : null}
    </section>
  )
}
